import React from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { FormikActions } from 'formik';
import {
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { useLocation } from '@tanstack/react-router';
import { RouterService } from '@piwikpro/router-crate';
import {
  ModalView,
  Stack,
  Form,
  TextFieldControl,
  CheckboxControl,
  FilePicker,
  Text,
  Card,
  Information,
  withTranslation,
  HelpLink,
  IconName,
  formatNumber,
} from '@piwikpro/ui-components';
import { FormValidator } from '@piwikpro/form-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { DomainTypes } from '../../redux/list';
import { DomainsService } from '../../DomainsService';

class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

const mapStateToProps = (state: {
  session: {
    organizationName: SessionState['organizationName']
  }
}) => ({
  organizationName: state.session.organizationName,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;


interface FileDetails {
  text: string
  additionalInfo: string
  preview: {
    type: 'icon'
    data: IconName
  }
  rawFile: File
}

interface FormValues {
  name: string
  types: DomainTypes[]
  certificate: { key: { fileAsBase64: string }, chain: { fileAsBase64: string } }
}

interface InjectedProps {
  validation: FormValidator
  domains: DomainsService
  Title: typeof React.Component
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

interface FormikErrors {
  chain?: string | null
  key?: string | null
}

const toBase64 = (file: File) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

export const AddDomain: React.FC<Props> = ({
  domains,
  validation,
  t,
  Title,
  organizationName,
  routerService,
}) => {
  const location = useLocation();

  const handleCancel = () => {
    const parentPath = location.pathname.replace(/\/m\/.*$/, '') || '/administration/account/domains';
    routerService.push(parentPath);
  };

  const handleSubmit = async (
    values: FormValues,
    { setErrors, setSubmitting }: FormikActions<FormValues>,
  ) => {
    try {
      await domains.addDomain({
        name: values.name,
        types: values.types,
        certificate: {
          chain: values.certificate.chain.fileAsBase64,
          key: values.certificate.key.fileAsBase64,
        },
      });
      handleCancel();
      await domains.getDomainsList();
      // refetch to update new cert status under the hood
      await domains.getDomainsList({ refetchCertStatusOfDomain: values.name });
    } catch (err: any) {
      setErrors(err.errors);
      setSubmitting(false);
    }
  };

  const handleCheckboxChange = (
    values: FormValues,
    value: DomainTypes,
    setFieldValue: FormikActions<FormValues>['setFieldValue'],
  ) => {
    if (!values.types.includes(value)) {
      setFieldValue(
        'types',
        [...values.types, value],
      );
    } else {
      setFieldValue(
        'types',
        values.types.filter(type => type !== value),
      );
    }
  };

  const handleFileUpload = async (acceptedFiles: File[], type: 'key' | 'chain', values, setFieldValue, setErrors, errors) => {
    const file = acceptedFiles[0];
    let errorMessage: null | string = null;

    try {
      if (acceptedFiles.length === 0) {
        if (type === 'key') {
          throw new ValidationError('domains.addDomain.cert.upload.validation.key');
        } else if (type === 'chain') {
          throw new ValidationError('domains.addDomain.cert.upload.validation.chain');
        }
      }
    } catch (err: any) {
      if (err.name === 'ValidationError') {
        errorMessage = err.message;
      }

      setErrors({
        files: {
          ...errors.files,
          [type]: errorMessage,
        },
      });

      return;
    }

    const fileSizeFormattedTokB = `${formatNumber(
      Number(file.size / 1000),
    )}kB`;

    const fileDetails: FileDetails = {
      text: file.name,
      additionalInfo: fileSizeFormattedTokB,
      preview: {
        type: 'icon',
        data: 'page',
      },
      rawFile: file,
    };

    try {
      setFieldValue('certificate', {
        ...values.certificate,
        [type]: {
          ...fileDetails,
          fileAsBase64: (await toBase64(file) as string).split('base64,')[1],
        },
      });
    } catch {
      setErrors({
        files: {
          ...errors.files,
          [type]: 'domains.addDomain.cert.upload.validation.fileProcessingError',
        },
      });
    }
  };

  return (
    <Title part={t('platform.domains.add-domain.title')} subset>
      <Form
        id="add-domain-form"
        initialValues={{
          name: '',
          types: [],
          certificate: {
            chain: null,
            key: null,
          },
        }}
        validationSchema={validation.createValidationSchema(validators => ({
          name: validators.domains.name,
          // @ts-ignore
          types: validators.domains.types,
          // @ts-ignore
          certificate: validators.domains.certificate,
        }))}
        onSubmit={handleSubmit}
        render={({
          values, setFieldValue, setErrors, isSubmitting, errors,
        }) => (
          <ModalView
            title={t('platform.domains.add-domain.title')}
            actions={(
              <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
                <Form.Cancel onClick={handleCancel} />
                <Form.Submit text={t('platform.domains.add-domain.add-button')} />
              </Stack>
            )}
          >
            <Stack vertical>
              {errors.detail && (
                <Information type="error">{errors.detail as string}</Information>
              )}
              <Stack.Item fill>
                <TextFieldControl
                  label={t('platform.domains.add-domain.name.label')}
                  info={t('platform.domains.add-domain.name.info', { organization: organizationName })}
                  placeholder="example.com"
                  name="name"
                  autoFocus
                  required
                />
              </Stack.Item>
              <Stack.Item fill>
                <Stack spacing="narrow" vertical>
                  <Stack.Item>
                    <Text as="p" weight={600} size={12} dataId="Label">{t('platform.domains.add-domain.types-general.label')}</Text>
                  </Stack.Item>
                  <Stack.Item previousSpacing="extraNarrow">
                    <CheckboxControl
                      name="types.ui_apis"
                      label={t('platform.domains.add-domain.types-uis-apis.label')}
                      value="ui_apis"
                      checked={values.types.includes('ui_apis')}
                      onChange={({ target: { value } }) => (
                        handleCheckboxChange(values, value, setFieldValue)
                      )}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CheckboxControl
                      name="types.static_resources"
                      label={t('platform.domains.add-domain.types-statics.label')}
                      value="static_resources"
                      checked={values.types.includes('static_resources')}
                      onChange={({ target: { value } }) => (
                        handleCheckboxChange(values, value, setFieldValue)
                      )}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CheckboxControl
                      name="types.tracking_dynamic_endpoints"
                      label={t('platform.domains.add-domain.types-tracking-endpoints.label')}
                      value="tracking_dynamic_endpoints"
                      checked={values.types.includes('tracking_dynamic_endpoints')}
                      onChange={({ target: { value } }) => (
                        handleCheckboxChange(values, value, setFieldValue)
                      )}
                    />
                  </Stack.Item>
                </Stack>
              </Stack.Item>
              <Stack.Item fill>
                <Stack vertical horizontalAlignment="middle">
                  <Stack.Item fill>
                    <Text as="p" weight={600} size={12} dataId="Label">{t('platform.domains.add-domain.certificate-general.label')}</Text>
                  </Stack.Item>
                  <Stack.Item previousSpacing="extraNarrow" fill>
                    <Card>
                      <Card.Section padding="narrow" subdued>
                        <Stack vertical>
                          <Stack.Item>
                            <Text as="p" weight={600} size={12} dataId="Label">
                              {t('platform.domains.add-domain.certificate-chain.label')}
                              <Text as="span" color="Red">*</Text>
                            </Text>
                          </Stack.Item>
                          <Stack.Item previousSpacing="extraNarrow" fill>
                            <FilePicker
                              type="horizontal"
                              accept={['.crt', '.pem', '.cer']}
                              fileDetails={values.certificate.chain}
                              onDrop={acceptedFiles => handleFileUpload(acceptedFiles, 'chain', values, setFieldValue, setErrors, errors)}
                              onCancelOrRemoveClick={
                                () => setFieldValue('certificate', {
                                  ...values.certificate,
                                  chain: null,
                                })
                              }
                              error={t((errors.files as FormikErrors)?.chain as string)}
                              disabled={isSubmitting}
                            />
                          </Stack.Item>
                          <Stack.Item>
                            <Text as="p" weight={600} size={12} dataId="Label">
                              {t('platform.domains.add-domain.certificate-key.label')}
                              <Text as="span" color="Red">*</Text>
                            </Text>
                          </Stack.Item>
                          <Stack.Item previousSpacing="extraNarrow" fill>
                            <FilePicker
                              type="horizontal"
                              accept={['.key', '.pem']}
                              fileDetails={values.certificate.key}
                              onDrop={acceptedFiles => handleFileUpload(acceptedFiles, 'key', values, setFieldValue, setErrors, errors)}
                              onCancelOrRemoveClick={
                                () => setFieldValue('certificate', {
                                  ...values.certificate,
                                  key: null,
                                })
                              }
                              error={t((errors.files as FormikErrors)?.key as string)}
                              disabled={isSubmitting}
                            />
                          </Stack.Item>
                        </Stack>
                      </Card.Section>
                    </Card>
                  </Stack.Item>
                  <Stack.Item>
                    <HelpLink href="https://help.piwik.pro/support/account/set-up-a-custom-domain/">
                      Set up a custom domain
                    </HelpLink>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </ModalView>
        )}
      />
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  validation: 'FormCrate.validation',
  domains: 'DomainsCrate.DomainsService',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  withTranslation(['administration', 'platform'])(
    errorBoundary(AddDomain),
  ),
));
